import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import AxiosInstance from "@/utils/AxiosInstance";
import { DEVICE_TAB_CONFIG, type PatientReadingTab } from "./deviceTabConfig";
import type { DeviceV2 } from "../types";

function deviceTypesFromList(devices: DeviceV2[]): string[] {
  return Array.from(
    new Set(
      devices
        .map((device) => String(device?.deviceType ?? device?.type ?? ""))
        .filter(Boolean),
    ),
  );
}

function computeVisibleTabs(
  showOnlyAssignedDeviceTabs: boolean,
  assignedTypes: string[],
  tabsWithReadings: string[],
): PatientReadingTab[] {
  return DEVICE_TAB_CONFIG.filter((tab) => {
    if (tab.isDeviceTab) return true;
    if (tab.isPulseRateTab) {
      if (!showOnlyAssignedDeviceTabs) return true;
      return (
        assignedTypes.includes("blood_pressure") ||
        tabsWithReadings.includes("blood_pressure")
      );
    }
    if (showOnlyAssignedDeviceTabs) {
      return tab.deviceType
        ? assignedTypes.includes(tab.deviceType) ||
            tabsWithReadings.includes(tab.deviceType)
        : false;
    }
    return true;
  });
}

type Options = {
  patientId?: string;
  isActive: boolean;
  showOnlyAssignedDeviceTabs?: boolean;
  isCaretaker?: boolean;
};

export function useDevicesTabData({
  patientId,
  isActive,
  showOnlyAssignedDeviceTabs = false,
  isCaretaker = false,
}: Options) {
  const [devices, setDevices] = useState<DeviceV2[]>([]);
  const [resolvedTimezone, setResolvedTimezone] = useState("");
  const [tabsWithReadings, setTabsWithReadings] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);
  const loadedPatientRef = useRef<string | null>(null);
  const fetchGenRef = useRef(0);

  const load = useCallback(async (pid: string, force = false) => {
    if (!force && loadedPatientRef.current === pid) return;

    const gen = ++fetchGenRef.current;
    setLoading(true);

    try {
      const { data: deviceRes } = await AxiosInstance().get<{
        data?: { devices?: DeviceV2[]; resolvedTimezone?: string };
      }>("/devices", { params: { patientId: pid } });

      if (gen !== fetchGenRef.current) return;

      const deviceList = deviceRes?.data?.devices ?? [];
      console.log("[useDevicesTabData] GET /devices response", deviceRes);
      console.log("[useDevicesTabData] devices list", deviceList);
      setDevices(deviceList);
      setResolvedTimezone(deviceRes?.data?.resolvedTimezone ?? "");

      const deviceTypesToCheck = Array.from(
        new Set(
          DEVICE_TAB_CONFIG.map((tab) => tab.deviceType).filter(
            (deviceType): deviceType is string => Boolean(deviceType),
          ),
        ),
      );

      const results = await Promise.allSettled(
        deviceTypesToCheck.map((deviceType) =>
          AxiosInstance().get(`/devices/patient/${pid}/readings`, {
            params: { deviceType, limit: 1 },
          }),
        ),
      );

      if (gen !== fetchGenRef.current) return;

      const withReadings: string[] = [];
      results.forEach((result, index) => {
        if (result.status !== "fulfilled") return;
        const response = result.value as {
          data?: { data?: { readings?: unknown[] }; readings?: unknown[] };
        };
        const readings =
          response?.data?.data?.readings ?? response?.data?.readings ?? [];
        if (Array.isArray(readings) && readings.length > 0) {
          withReadings.push(deviceTypesToCheck[index]);
        }
      });

      setTabsWithReadings(withReadings);
      loadedPatientRef.current = pid;
      setReady(true);
    } catch {
      if (gen === fetchGenRef.current) {
        setTabsWithReadings([]);
        setReady(true);
      }
    } finally {
      if (gen === fetchGenRef.current) {
        setLoading(false);
      }
    }
  }, []);

  useEffect(() => {
    if (!patientId) {
      loadedPatientRef.current = null;
      setDevices([]);
      setTabsWithReadings([]);
      setResolvedTimezone("");
      setReady(false);
      return;
    }
    if (loadedPatientRef.current !== patientId) {
      loadedPatientRef.current = null;
      setReady(false);
    }
  }, [patientId]);

  useEffect(() => {
    if (!isActive || !patientId) return;
    void load(patientId);
  }, [isActive, patientId, load]);

  const assignedTypesKey = useMemo(
    () => deviceTypesFromList(devices).sort().join("|"),
    [devices],
  );
  const tabsWithReadingsKey = tabsWithReadings.join("|");

  const visibleTabs = useMemo(() => {
    const assignedTypes = assignedTypesKey ? assignedTypesKey.split("|") : [];
    return computeVisibleTabs(
      showOnlyAssignedDeviceTabs,
      assignedTypes,
      tabsWithReadings,
    );
  }, [showOnlyAssignedDeviceTabs, assignedTypesKey, tabsWithReadingsKey]);

  const hasDeviceData = devices.length > 0 || tabsWithReadings.length > 0;

  const resolveDefaultTab = useCallback(
    (currentTab: string) => {
      if (
        showOnlyAssignedDeviceTabs &&
        visibleTabs.length > 0 &&
        !visibleTabs.some((tab) => tab.value === currentTab)
      ) {
        return visibleTabs[0].value;
      }
      return currentTab;
    },
    [showOnlyAssignedDeviceTabs, visibleTabs],
  );

  const refreshDevices = useCallback(async () => {
    if (!patientId) return;
    await load(patientId, true);
  }, [patientId, load]);

  return {
    devices,
    resolvedTimezone,
    visibleTabs,
    hasDeviceData,
    loading,
    ready,
    refreshDevices,
    resolveDefaultTab,
  };
}
